<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div
    class="promo-banner critical-component-hide"
    :class="{
      'promo-banner--gradient': settings.gradientColor
    }"
    :style="`
      --promo-banner-background-color: rgba(${settings.backgroundColor});
      --promo-banner-text-color: rgb(${settings.textColor});
      --promo-banner-hyperlink-color: rgb(${settings.hyperlinkColor});
      ${settings.gradientColor ?
    `--promo-banner-gradient-color: rgba(${settings.gradientColor});` : ''}
    `"
    role="region"
    :aria-label="$string('general.site_header.accessibility.promo_banner_aria_label')"
    @mouseenter.prevent="isHovering = true"
    @mouseleave.prevent="isHovering = false"
    @focus.prevent="isFocused = true"
    @blur.prevent="isFocused = false"
    @touchstart.passive="isHovering = true"
    @touchend.passive="state = isHovering = false"
  >
    <span
      id="promo-banner-marquee-label"
      class="visually-hidden"
      v-text="$string('general.site_header.promo_banner')"
    />

    <ul
      class="promo-banner__list"
      role="marquee"
      aria-labelledby="promo-banner-marquee-label"
    >
      <li
        v-for="(block, index) of blocks"
        :key="`promo-${index}`"
        ref="banners"
        class="promo-banner__list-item text-body-xs-regular"
        :class="{
          'is-current': index === currentBlockIndex
        }"
      >
        <span
          v-if="index === currentBlockIndex"
          class="promo-banner__list-item-content"
          v-html="getBlockMarkup(block.title)"
        />
        <span
          v-else
          class="promo-banner__list-item-content"
          v-text="block.title"
        />
      </li>
    </ul>
    <btn
      v-if="autoscroll.enable && blocks.length > 1"
      :aria-label="`${
        isPaused
          ? $string('accessibility.marquee.play')
          : $string('accessibility.marquee.pause')
      } - ${$string('general.site_header.promo_banner')}`"
      label=""
      type="button"
      modifiers="text"
      class="promo-banner__navigation"
      show-icon
      @click.prevent="isPaused = !isPaused"
    >
      <template #icon>
        <icon-play v-if="isPaused" />
        <icon-pause v-if="!isPaused" />
      </template>
    </btn>
  </div>
</template>

<script>
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'
import Btn from '~global/btn/btn'

/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - No style import as it's included in critical stylesheet.
 * - CSS is used for fading through the blocks.
 * - For more advanced functionality add carousel.
 *
 * @param {Object} autoscroll - Autoscroll settings.
 * @param {Array} blocks - Promo blocks.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 *
 */

export default {
  name: 'PromoBanner',

  components: {
    Btn,
    IconPause,
    IconPlay,
  },

  props: {
    autoscroll: {
      type: Object,
      default: () => ({
        enable: false,
        timing: 5,
      }),
    },
    settings: {
      type: Object,
      default: () => ({
        backgroundColor: '',
        hyperlinkColor: '',
        textColor: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      interval: null,
      isFocused: false,
      isHovering: false,
      isPaused: false,
      currentBlockIndex: 0,
    }
  },

  watch: {
    currentBlockIndex: {
      handler(newIndex) {
        this.$nextTick().then(async() => {
          const bannerHeight = this.$refs.banners[newIndex]?.closest('[data-handle="promo-banner"]').offsetHeight
          document.documentElement.style.setProperty(
            '--promo-banner-height-static',
            `${bannerHeight}px`,
          )

          await this.$nextTick()
          cnvs.EventBus.emit('cnvs:update-site-header-height')
        })
      },
      immediate: true,
    },
  },

  async mounted() {
    await this.$nextTick()
    cnvs.EventBus.emit('cnvs:update-site-header-height')

    if (this.autoscroll.enable) {
      this.interval = setInterval(() => {
        if (!this.isFocused && !this.isHovering && !this.isPaused) {
          this.currentBlockIndex =
            this.currentBlockIndex === this.blocks.length - 1
              ? 0
              : this.currentBlockIndex + 1
        }
      }, this.autoscroll.timing * 1000)
    }
  },

  beforeUnmount() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },

  methods: {

    /**
     * Get block markup.
     * - Applies class to certain elements.
     * @param {String} block - Block text.
     * @returns {String}
     */
    getBlockMarkup(block) {
      return this.$localizeRichtext(
        block
          .replaceAll('<a', '<a class="text-body-xs-regular"')
          .replaceAll('<strong', '<strong class="text-body-xs-bold"'),
      )
    },
  },
}
</script>
